/**
 * BCR Dashboard component — charts and statistics for BCR test overview.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Card, Row, Col, Statistic, Select, Segmented, Empty, Spin } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { useBcrStore } from '@/store/bcrStore';
import { useConfigStore } from '@/store/configStore';

const BcrDashboard: React.FC = () => {
  const {
    statsSummary,
    statsTimeline,
    statsStageFailures,
    statsFailureReasons,
    isStatsLoading,
    fetchStats,
  } = useBcrStore();
  const { projects, fetchProjects } = useConfigStore();

  const [granularity, setGranularity] = useState<'week' | 'month'>('week');
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(undefined);
  const [chartReady, setChartReady] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load projects for filter dropdown
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Fetch stats when filters change
  useEffect(() => {
    fetchStats({
      granularity,
      project_id: selectedProjectId,
    });
  }, [granularity, selectedProjectId, fetchStats]);

  // Delay chart rendering to ensure DOM is ready
  useEffect(() => {
    if (!statsSummary || statsSummary.total_tasks === 0) {
      setChartReady(false);
      return;
    }
    setChartReady(false);
    let rafId1: number;
    let rafId2: number;
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        if (isMountedRef.current) {
          setChartReady(true);
        }
      });
    });
    return () => {
      cancelAnimationFrame(rafId1);
      cancelAnimationFrame(rafId2);
    };
  }, [statsSummary, statsTimeline, statsStageFailures, statsFailureReasons]);

  const handleProjectChange = useCallback((value: number | undefined) => {
    setSelectedProjectId(value);
  }, []);

  const handleGranularityChange = useCallback((value: string | number) => {
    setGranularity(value as 'week' | 'month');
  }, []);

  // Loading state
  if (isStatsLoading && !statsSummary) {
    return (
      <div className="text-center py-12">
        <Spin size="large" />
      </div>
    );
  }

  // Empty state
  if (!statsSummary || statsSummary.total_tasks === 0) {
    return (
      <div>
        <div className="mb-4 flex items-center gap-4">
          <Segmented
            options={[
              { label: '按周', value: 'week' },
              { label: '按月', value: 'month' },
            ]}
            value={granularity}
            onChange={handleGranularityChange}
          />
          <Select
            placeholder="全部项目"
            allowClear
            style={{ width: 240 }}
            value={selectedProjectId}
            onChange={handleProjectChange}
            options={projects.map((p) => ({ label: p.name, value: p.id }))}
          />
        </div>
        <Empty description="暂无BCR任务数据" />
      </div>
    );
  }

  const { summary, timeline, stageFailures, failureReasons } = {
    summary: statsSummary,
    timeline: statsTimeline,
    stageFailures: statsStageFailures,
    failureReasons: statsFailureReasons,
  };

  // Chart option: Timeline bar chart
  const timelineOption = {
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
    },
    legend: {
      data: ['成功', '失败'],
      bottom: 0,
    },
    grid: { left: 40, right: 20, top: 20, bottom: 40 },
    xAxis: {
      type: 'category' as const,
      data: timeline.map((item) => item.period),
      axisLabel: { rotate: 45, fontSize: 11 },
    },
    yAxis: {
      type: 'value' as const,
      name: '任务数',
      minInterval: 1,
    },
    series: [
      {
        name: '成功',
        type: 'bar',
        stack: 'total',
        data: timeline.map((item) => item.success),
        itemStyle: { color: '#52c41a' },
        emphasis: { focus: 'series' as const },
      },
      {
        name: '失败',
        type: 'bar',
        stack: 'total',
        data: timeline.map((item) => item.failed),
        itemStyle: { color: '#d9776f' },
        emphasis: { focus: 'series' as const },
      },
    ],
  };

  // Chart option: Success rate donut
  const pieOption = {
    tooltip: {
      trigger: 'item' as const,
      formatter: '{b}: {c} ({d}%)',
    },
    legend: {
      orient: 'horizontal' as const,
      bottom: 0,
    },
    series: [
      {
        name: '任务结果',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 6,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: true,
          formatter: '{b}: {c}',
        },
        data: [
          {
            value: summary.finished_tasks,
            name: '成功',
            itemStyle: { color: '#52c41a' },
          },
          {
            value: summary.failed_tasks,
            name: '失败',
            itemStyle: { color: '#d9776f' },
          },
        ],
      },
    ],
  };

  // Chart option: Stage failures horizontal bar
  const stageFailOption = {
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
    },
    grid: { left: 120, right: 40, top: 10, bottom: 20 },
    xAxis: {
      type: 'value' as const,
      name: '失败次数',
      minInterval: 1,
    },
    yAxis: {
      type: 'category' as const,
      data: stageFailures.map((s) => s.stage_label),
      inverse: true,
      axisLabel: { fontSize: 11 },
    },
    series: [
      {
        name: '失败次数',
        type: 'bar',
        data: stageFailures.map((s) => ({
          value: s.count,
          itemStyle: { color: '#e89880' },
        })),
        label: {
          show: true,
          position: 'right' as const,
          fontSize: 11,
        },
      },
    ],
  };

  // Chart option: Failure reasons horizontal bar
  const reasonsOption = {
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      formatter: (params: { value: number; name: string }[]) => {
        const p = params[0];
        return `${p.name}<br/>失败次数: ${p.value}`;
      },
    },
    grid: { left: 10, right: 40, top: 10, bottom: 20 },
    xAxis: {
      type: 'value' as const,
      name: '失败次数',
      minInterval: 1,
    },
    yAxis: {
      type: 'category' as const,
      data: failureReasons.map((r) =>
        r.reason.length > 40 ? r.reason.substring(0, 40) + '...' : r.reason,
      ),
      inverse: true,
      axisLabel: { fontSize: 10 },
    },
    series: [
      {
        name: '失败次数',
        type: 'bar',
        data: failureReasons.map((r) => ({
          value: r.count,
          itemStyle: { color: '#faad14' },
        })),
        label: {
          show: true,
          position: 'right' as const,
          fontSize: 11,
        },
      },
    ],
  };

  const chartKey = `bcr-${summary.total_tasks}-${granularity}-${selectedProjectId || 'all'}`;

  return (
    <div>
      {/* Controls */}
      <div className="mb-4 flex items-center gap-4">
        <Segmented
          options={[
            { label: '按周', value: 'week' },
            { label: '按月', value: 'month' },
          ]}
          value={granularity}
          onChange={handleGranularityChange}
        />
        <Select
          placeholder="全部项目"
          allowClear
          style={{ width: 240 }}
          value={selectedProjectId}
          onChange={handleProjectChange}
          options={projects.map((p) => ({ label: p.name, value: p.id }))}
        />
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="BCR任务总数"
              value={summary.total_tasks}
              prefix={<ExperimentOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="成功率"
              value={summary.success_rate}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: summary.success_rate >= 80 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="失败任务"
              value={summary.failed_tasks}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: summary.failed_tasks > 0 ? '#d9776f' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="平均耗时"
              value={summary.avg_duration_minutes}
              suffix="分钟"
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Row 1: Timeline + Success Rate */}
      {!chartReady ? (
        <div className="flex items-center justify-center" style={{ height: 350 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24} lg={14}>
              <Card title={granularity === 'week' ? '每周BCR任务趋势' : '每月BCR任务趋势'}>
                <div style={{ height: 320, width: '100%' }}>
                  <ReactECharts
                    key={`${chartKey}-timeline`}
                    option={timelineOption}
                    style={{ height: '100%', width: '100%' }}
                    opts={{ renderer: 'svg' }}
                    notMerge
                    lazyUpdate
                  />
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={10}>
              <Card title="成功率分布">
                <div style={{ height: 320, width: '100%' }}>
                  <ReactECharts
                    key={`${chartKey}-pie`}
                    option={pieOption}
                    style={{ height: '100%', width: '100%' }}
                    opts={{ renderer: 'svg' }}
                    notMerge
                    lazyUpdate
                  />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Charts Row 2: Stage Failures + Failure Reasons */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="各阶段失败分布">
                {stageFailures.length === 0 ? (
                  <Empty description="暂无阶段失败数据" />
                ) : (
                  <div style={{ height: Math.max(280, stageFailures.length * 40), width: '100%' }}>
                    <ReactECharts
                      key={`${chartKey}-stage`}
                      option={stageFailOption}
                      style={{ height: '100%', width: '100%' }}
                      opts={{ renderer: 'svg' }}
                      notMerge
                      lazyUpdate
                    />
                  </div>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Top 10 失败原因">
                {failureReasons.length === 0 ? (
                  <Empty description="暂无失败原因数据" />
                ) : (
                  <div style={{ height: Math.max(280, failureReasons.length * 40), width: '100%' }}>
                    <ReactECharts
                      key={`${chartKey}-reasons`}
                      option={reasonsOption}
                      style={{ height: '100%', width: '100%' }}
                      opts={{ renderer: 'svg' }}
                      notMerge
                      lazyUpdate
                    />
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default BcrDashboard;
